import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AsyncSection } from '@/app/components/AsyncSection';
import { Alert } from '@/app/components/ui/Alert';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { Field, Input, Select } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { renderLabel } from '@/i18n/renderLabel';
import { formatDateTime } from '@/lib/format';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';
import { AdminSubmitError } from '@/manage/subscriptions/errors';
import {
  getProviderConfig,
  getWebhookUrls,
  PROVIDER_CONFIG_FIELDS,
  PROVIDER_LABEL_KEYS,
  PROVIDER_WEBHOOK_KEYS,
  testProviderConnection,
  updateProviderConfig,
  type SubscriptionProvider,
  type SubscriptionProviderConfig,
  type SubscriptionTestConnectionResult,
  type SubscriptionWebhookUrls,
} from '@/manage/subscriptions/api';

const PROVIDERS: readonly SubscriptionProvider[] = ['apple', 'google', 'mollie'];

/**
 * "Beheer › Abonnementen › Providerconfiguratie" (§ 2, `SubscriptionProviderConfig`):
 * one fixed form per provider — never a free key/value editor, since the
 * valid keys are fixed server-side. Secret fields are always shown masked;
 * the input never gets prefilled with a real secret, an empty value on save
 * means "keep the stored one". Each provider's own webhook URLs (from the
 * one `webhook-urls` endpoint, fetched once here and filtered per provider)
 * are shown right below its form — there is no separate Webhooks screen.
 */
export function ConfigPage() {
  const { t } = useTranslation();
  const [provider, setProvider] = useState<SubscriptionProvider>('apple');
  const webhookUrls = useAsync(() => getWebhookUrls(), []);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader
        title={t('manageSubscriptions.config.title')}
        lead={t('manageSubscriptions.config.lead')}
      />

      <div className="flex gap-2" role="tablist">
        {PROVIDERS.map((p) => (
          <Button
            key={p}
            type="button"
            variant={p === provider ? 'primary' : 'secondary'}
            size="sm"
            role="tab"
            aria-selected={p === provider}
            onClick={() => setProvider(p)}
          >
            {renderLabel(t, PROVIDER_LABEL_KEYS[p])}
          </Button>
        ))}
      </div>

      <ProviderConfigCard
        key={provider}
        provider={provider}
        webhookUrls={webhookUrls.data ?? undefined}
      />
    </section>
  );
}

function ProviderConfigCard({
  provider,
  webhookUrls,
}: {
  provider: SubscriptionProvider;
  webhookUrls: SubscriptionWebhookUrls | undefined;
}) {
  const { t } = useTranslation();
  const config = useAsync(() => getProviderConfig(provider), [provider]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<SubscriptionTestConnectionResult | null>(null);

  const save = useSubmit(async () => {
    await updateProviderConfig(provider, values);
    setValues({});
    config.reload();
  });

  const test = useSubmit(async () => {
    setTestResult(await testProviderConnection(provider));
  });

  return (
    <Card>
      <CardHeader
        title={renderLabel(t, PROVIDER_LABEL_KEYS[provider])}
        action={
          <Button
            variant="secondary"
            size="sm"
            disabled={test.pending}
            onClick={() => void test.submit()}
          >
            {t('manageSubscriptions.config.testConnection')}
          </Button>
        }
      />

      {testResult && (
        <Alert tone={testResult.ok ? 'success' : 'danger'} className="mb-4">
          {testResult.ok
            ? t('manageSubscriptions.config.testResultOk', { message: testResult.message ?? '' })
            : t('manageSubscriptions.config.testResultFailed', {
                message: testResult.message ?? '',
              })}
        </Alert>
      )}
      <AdminSubmitError error={test.error} />

      <AsyncSection state={config} label={t('manageSubscriptions.config.title')}>
        {(data: SubscriptionProviderConfig) => (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              void save.submit();
            }}
          >
            {PROVIDER_CONFIG_FIELDS[provider].map(({ key, secret, labelKey, options }) => {
              const field = data[key];
              const value = values[key] ?? (secret ? '' : (field?.value ?? ''));
              return (
                <Field
                  key={key}
                  label={renderLabel(t, labelKey)}
                  hint={
                    secret ? (
                      t('manageSubscriptions.config.secretHint', {
                        masked: field?.value ?? t('manageSubscriptions.config.noValueYet'),
                      })
                    ) : field?.updated_by ? (
                      <>
                        {t('manageSubscriptions.config.updatedByBefore')}{' '}
                        <Link to={`/users/${field.updated_by}`} className="underline">
                          {field.updated_by}
                        </Link>{' '}
                        {t('manageSubscriptions.config.updatedByAfter', {
                          when: field.updated_at ? formatDateTime(field.updated_at) : '—',
                        })}
                      </>
                    ) : undefined
                  }
                >
                  {options ? (
                    <Select
                      value={value}
                      onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                    >
                      <option value="" disabled>
                        {t('manageSubscriptions.config.selectPlaceholder')}
                      </option>
                      {options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      type={secret ? 'password' : 'text'}
                      autoComplete="off"
                      placeholder={secret ? '' : (field?.value ?? '')}
                      value={value}
                      onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                    />
                  )}
                </Field>
              );
            })}

            <div className="flex items-center justify-end gap-3">
              <AdminSubmitError error={save.error} />
              <Button type="submit" disabled={save.pending}>
                {t('manageSubscriptions.config.save')}
              </Button>
            </div>
          </form>
        )}
      </AsyncSection>

      <div className="border-line mt-6 flex flex-col gap-3 border-t pt-6">
        <h3 className="text-ink text-sm font-medium">{t('manageSubscriptions.webhooks.title')}</h3>
        <ul className="flex flex-col gap-3">
          {PROVIDER_WEBHOOK_KEYS[provider].map((key) => (
            <WebhookRow
              key={key}
              label={t(`manageSubscriptions.webhooks.key_${key}`)}
              url={webhookUrls?.[key]}
            />
          ))}
        </ul>
      </div>
    </Card>
  );
}

function WebhookRow({ label, url }: { label: string; url: string | undefined }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  return (
    <li className="border-line flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3">
      <div>
        <p className="text-ink text-sm font-medium">{label}</p>
        <p className="text-accent text-[13px] break-all">{url ?? '—'}</p>
      </div>
      <Button
        variant="secondary"
        size="sm"
        disabled={!url}
        onClick={() => {
          if (!url) return;
          void navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          });
        }}
      >
        {copied ? t('manageSubscriptions.webhooks.copied') : t('manageSubscriptions.webhooks.copy')}
      </Button>
    </li>
  );
}
